import React from 'react';
import { FormattedDate } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { IWithReactRouterProps } from '../../mixins/router/withReactRouter';
import { ITranslateProps, translate } from '../../mixins/translate';
import { fetchDelete, fetchGet } from '../../store/fetch';
import { Roles } from '../../utils/enums/Roles';
import { IconEditButton } from '../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import {
    Content,
    EditPanelButton,
    EditPanelInput,
    EditPanelSection,
    PanelBody,
    PanelWithoutForm,
    RightTray,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    Tray,
} from '../elements';
import { ComponentSetStatePromised } from '../utils/ComponentSetStatePromised';
import { ListPager } from '../utils/ListPager';
import { NullableBoolean } from '../utils/NullableBoolean';
import RoleDependent from '../utils/RoleDependent';

interface ICompaniesListProps extends ITranslateProps, IWithReactRouterProps {
    dispatch: (prop: any) => void;
    location: any;
}

export interface ICompany {
    id: string;
    name: string;
    storageLimit: number;
    storageUsed: number;
    registeredOn: Date;
    usersCount: number;
    role: string;
    isClient: boolean;
}

interface ICompaniesListState {
    failed: boolean;
    errorCode?: string;
    searching: boolean;
    companies: ICompany[];
    total: number;
    page: number;
    companyName?: string;
    userEmail?: string;
}

function mapStateToProps() {
    return {};
}

const itemsPerPage = 50;

class CompaniesListComponent extends ComponentSetStatePromised<ICompaniesListProps, ICompaniesListState> {
    constructor(props: ICompaniesListProps) {
        super(props);

        this.state = {
            failed: false,
            searching: false,
            companies: [],
            total: 0,
            page: 1,
        };
    }

    public override componentDidMount() {
        void this.search(this.props.location.query.page);
    }

    public override render() {
        return (
            <div>
                <header id="topbar" className="ph10">
                    <div className="topbar-left">
                        <ol className="breadcrumb">
                            <li className="crumb-active">
                                <span className="fa fa-building pr10"></span>
                                <Link to="/companies">{this.props.t('common.companies')}</Link>
                            </li>
                        </ol>
                    </div>
                    <div className="topbar-right hidden-xs hidden-sm">
                        <Link to="/companies/add" className="btn btn-default btn-sm fw600 ml10">
                            <span className="fa fa-plus pr5"></span> {this.props.t('common.addItem', this.props.t('common.company'))}
                        </Link>
                    </div>
                </header>

                <Content>
                    <Tray>
                        <PanelWithoutForm>
                            <PanelBody noPadding={true}>
                                <Table hover>
                                    <TableHead>
                                        <TableHeadRow light={true}>
                                            <TableHeader>{this.props.t('common.name')}</TableHeader>
                                            <TableHeader>{this.props.t('common.role')}</TableHeader>
                                            <RoleDependent
                                                allowed={[Roles.Administrator]}
                                                content={<TableHeader>{this.props.t('common.client')}</TableHeader>}
                                            />
                                            <TableHeader>{this.props.t('company.registeredOn')}</TableHeader>
                                            <TableHeader>{this.props.t('common.users')}</TableHeader>
                                            <TableHeader className="text-right">&nbsp; </TableHeader>
                                        </TableHeadRow>
                                    </TableHead>
                                    <TableBody>
                                        {this.state.companies.map((company) => (
                                            <TableRow onClick={() => this.props.navigate(`/companies/${company.id}`)} key={company.id}>
                                                <TableData>{company.name}</TableData>
                                                <TableData>{this.props.t(`common.${company.role}`)}</TableData>
                                                <RoleDependent
                                                    allowed={[Roles.Administrator]}
                                                    content={
                                                        <TableData>
                                                            <NullableBoolean value={company.isClient} />
                                                        </TableData>
                                                    }
                                                />
                                                <TableData>
                                                    <FormattedDate value={company.registeredOn} />{' '}
                                                </TableData>
                                                <TableData>{company.usersCount}</TableData>
                                                <td style={{ whiteSpace: 'nowrap', width: '80px' }}>
                                                    <RoleDependent
                                                        allowed={[Roles.Administrator, Roles.Client]}
                                                        content={
                                                            <>
                                                                <IconEditButton
                                                                    onClick={() => this.props.navigate(`/companies/${company.id}/edit`)}
                                                                />
                                                                <IconRemoveButton
                                                                    removeQuestion={this.props.t('common.confirmDelete', company.name)}
                                                                    onClick={async () => this.remove(company.id)}
                                                                />
                                                            </>
                                                        }
                                                    />
                                                </td>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </PanelBody>

                            <ListPager
                                disabled={this.state.searching}
                                itemsPerPage={itemsPerPage}
                                total={this.state.total}
                                page={this.state.page}
                                handleNavigation={this.handleNavigation.bind(this)}
                            />
                        </PanelWithoutForm>
                    </Tray>

                    <RightTray>
                        <form
                            id="filter"
                            onSubmit={(e) => {
                                e.preventDefault();
                                void this.search();
                            }}
                        >
                            <div className="admin-form">
                                <h4> {this.props.t('company.findCompany')}</h4>
                                <EditPanelSection topBorder={true}>
                                    <EditPanelInput
                                        name="companyName"
                                        placeholder={this.props.t('company.companyName')}
                                        value={this.state.companyName}
                                        icon="fa fa-building"
                                        handleChange={this.handleChange.bind(this)}
                                        handleEnter={this.search.bind(this)}
                                    />
                                </EditPanelSection>
                                <EditPanelSection>
                                    <EditPanelInput
                                        name="userEmail"
                                        placeholder={`${this.props.t('common.emailAddress')} ${this.props.t('common.user')}`}
                                        value={this.state.userEmail}
                                        icon="fa fa-user"
                                        handleChange={this.handleChange.bind(this)}
                                        handleEnter={this.search.bind(this)}
                                    />
                                </EditPanelSection>
                                <EditPanelSection topBorder={true}>
                                    <EditPanelButton
                                        onClick={async () => this.search()}
                                        text={this.props.t('common.search')}
                                        small={false}
                                        primary={true}
                                        disabled={this.state.searching}
                                    />
                                </EditPanelSection>
                            </div>
                        </form>
                    </RightTray>
                </Content>
            </div>
        );
    }

    private async remove(id: string) {
        const url = `/api/companies/${id}`;
        await fetchDelete(url);
        await this.search();
    }

    private async handleChange(value: any, stateKey: string) {
        const newState = {} as any;
        newState[stateKey] = value;
        await this.setStatePromised(newState);
    }

    private handleNavigation(page: number) {
        this.props.navigate({ pathname: '/companies', search: `?page=${page}` });
        void this.search(page.toString());
    }

    private async search(pageString?: string) {
        const pageNumber = pageString ? parseInt(pageString, 10) : 1;
        this.setState({ searching: true, page: pageNumber });

        let url = '/api/companies?';
        url += `limit=${itemsPerPage}`;
        url += `&offset=${itemsPerPage * (pageNumber - 1)}`;

        if (this.state.companyName) {
            url += `&name=${this.state.companyName}`;
        }
        if (this.state.userEmail) {
            url += `&email=${this.state.userEmail}`;
        }

        const result = await fetchGet(url);
        await this.setStatePromised({ total: result.json.total, companies: result.json.items, searching: false });
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const CompaniesList = translate()(connect(mapStateToProps)(CompaniesListComponent as any) as any);

export { CompaniesList };
